import type { ComponentProps, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { AnimatedTransition } from '@src/design-system/animated-transition';
import InfiniteScroll from '@src/design-system/infinite-scroll';

import { cn } from '../helpers';

import { SearchInput } from './search-input';
import type createCrudService from '@core/api/create-crud-service';

export type InfiniteListProps<
  T extends Record<string, unknown>,
  Q extends Record<string, unknown> = Record<string, unknown>,
> = {
  fetchInfiniteData: ReturnType<
    typeof createCrudService<T, Q>
  >['useFetchInfinite'];

  query?: Q;

  enableSearch?: boolean;
  searchParamKey?: string;
  searchParamName?: string;

  transformItems?: (items: T[]) => T[];

  /** Optional node rendered above the list (e.g., an “All” option) */
  allItem?: ReactNode;

  animateOnEveryRender?: boolean;

  props?: {
    searchContainerProps?: Partial<ComponentProps<'div'>>;
    searchInputProps?: Partial<ComponentProps<typeof SearchInput>>;

    listContainerProps?: Partial<ComponentProps<typeof AnimatedTransition>>;
    listProps?: Partial<ComponentProps<'div'>>;

    infiniteScrollProps?: Partial<ComponentProps<typeof InfiniteScroll>>;
  };
  renderItem?: (item: T) => ReactNode;
};

export const InfiniteList = <
  T extends Record<string, unknown>,
  Q extends Record<string, unknown>,
>({
  fetchInfiniteData,
  query,
  animateOnEveryRender,
  enableSearch,
  searchParamKey = 'name',
  searchParamName = 'name',
  transformItems,
  allItem,
  props,
  renderItem,
}: InfiniteListProps<T, Q>) => {
  const {
    searchContainerProps,
    infiniteScrollProps,
    searchInputProps,
    listContainerProps,
    listProps,
  } = props || {};
  const { className: searchContainerClassName, ...searchContainerRest } =
    searchContainerProps || {};
  const { className: listContainerClassName, ...listContainerRest } =
    listContainerProps || {};
  const { className: listClassName, ...listRest } = listProps || {};

  const [searchParams] = useSearchParams();
  const searchParamValue = searchParams.get(searchParamName);

  const {
    data,
    isPending,
    hasNextPage,
    fetchNextPage,
    isError,
    isFetchingNextPage,
  } = fetchInfiniteData({
    ...(searchParamValue && {
      [searchParamKey]: searchParamValue,
    }),
    ...(query as Q),
  });

  const items = data?.pages.flatMap((p) => p.results) || [];

  const loadingIndicator = (
    <div className='flex py-2 justify-center items-center'>
      <Loader2
        className='animate-spin text-primary-1000'
        size={24}
      />
    </div>
  );

  const emptyStateEl = (
    <div className='flex py-8 justify-center items-center'>
      <span className='text-center text-sm'>No results found.</span>
    </div>
  );

  const errorEl = (
    <div className='flex py-8 justify-center items-center'>
      <span className='text-center text-sm'>Something went wrong.</span>
    </div>
  );

  const finalItems = transformItems ? transformItems(items) : items;

  return (
    <>
      {enableSearch && (
        <div
          className={cn('mb-3 p-3 pb-0', searchContainerClassName)}
          {...searchContainerRest}
        >
          <SearchInput
            queryKey={searchParamName}
            placeholder='Search by name'
            {...searchInputProps}
          />
        </div>
      )}

      <AnimatedTransition
        isError={isError}
        error={errorEl}
        isPending={isPending}
        animateOnEveryRender={animateOnEveryRender}
        // transformItems can filter a page to nothing; keep the scroll sentinel
        // mounted while more pages exist so later matches still load.
        isFound={!isPending && (!!finalItems?.length || hasNextPage)}
        notFound={emptyStateEl}
        skeleton={loadingIndicator}
        className={cn(
          'max-h-[400px] p-3 space-y-2 min-h-[200px] pt-0 overflow-auto',
          listContainerClassName
        )}
        style={{ scrollbarWidth: 'none' }}
        {...listContainerRest}
      >
        <div
          className={cn('flex flex-col gap-2', listClassName)}
          {...listRest}
        >
          {allItem}
          {finalItems.map((item) => renderItem?.(item))}

          <InfiniteScroll
            hasMore={hasNextPage}
            isLoading={isFetchingNextPage}
            next={fetchNextPage}
            {...infiniteScrollProps}
          >
            {hasNextPage && loadingIndicator}
          </InfiniteScroll>
        </div>
      </AnimatedTransition>
    </>
  );
};
